import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#f4f4f4] py-12 md:py-20 px-6">
      <div className="max-w-4xl mx-auto bg-white border-[4px] border-black p-8 md:p-16 brutal-shadow text-left">
        <header className="border-b-[4px] border-black pb-8 mb-12">
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none mb-4 text-black">
            Privacy Policy
          </h1>
          <div className="inline-block bg-black text-white px-4 py-1 font-black uppercase text-sm">
            Effective Date: 27.07.2026
          </div>
        </header>

        <div className="space-y-12 font-medium text-lg leading-relaxed text-gray-800">
          <section>
            <p className="mb-6">
              Welcome to <strong>ETERA</strong>. We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you use our website and services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-black uppercase mb-6 text-black tracking-tight">
              1. Information We Collect
            </h2>
            <p className="mb-4">
              We believe in data minimization and only collect the information absolutely necessary to provide and improve our services.
            </p>
            <ul className="list-none space-y-4">
              <li className="pl-6 border-l-4 border-black">
                <span className="font-black uppercase text-sm block text-gray-500">For Link Visitors</span>
                When someone scans or visits an ETERA QR code, we record an anonymous scan count to provide basic usage statistics to the QR link owner. We do not collect, log, or store IP addresses or other personal identifiers during this process.
              </li>
              <li className="pl-6 border-l-4 border-black">
                <span className="font-black uppercase text-sm block text-gray-500">For Registered Users</span>
                When you create an account directly, we collect your email address and an encrypted password. If you choose to sign in using your Google account, we collect your email address, name, and unique Google ID provided to us by Google to verify your identity and manage your account.
              </li>
              <li className="pl-6 border-l-4 border-black">
                <span className="font-black uppercase text-sm block text-gray-500">For Newsletter Subscribers</span>
                If you opt-in to receive updates from us, we collect your email address to send you our newsletter.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-black uppercase mb-6 text-black tracking-tight">
              2. How We Use Your Data
            </h2>
            <p className="mb-4 text-sm font-black uppercase text-gray-400">Strict Usage Policy:</p>
            <ul className="list-disc pl-6 space-y-2 font-bold">
              <li>To create and manage your ETERA account.</li>
              <li>To provide anonymous QR code scan counts to link owners.</li>
              <li>To send administrative emails (e.g., password resets or service updates).</li>
              <li>To send marketing newsletters (only if you have explicitly opted in).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-black uppercase mb-6 text-black tracking-tight">
              3. Cookies
            </h2>
            <p>
              ETERA uses <strong>essential cookies only</strong>. These are required to maintain your active session and keep you logged into your account securely. We do not use advertising, tracking, or third-party analytics cookies on your device.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-black uppercase mb-6 text-black tracking-tight">
              4. Third-Party Service Providers
            </h2>
            <p className="mb-8 font-bold italic">
              We do not sell, rent, or share your personal data with marketing firms or data brokers. We only share necessary data with trusted third-party platforms:
            </p>
            
            <div className="space-y-6">
              <div className="p-6 bg-[#f9f9f9] border-2 border-black brutal-shadow-sm">
                <h3 className="font-black uppercase text-sm mb-2">Supabase (Database and Authentication)</h3>
                <p className="text-base">
                  To manage user accounts, authentication, and store your saved QR codes, we use Supabase, a service provided by Supabase, Inc. Your account information is transferred to and securely stored on Supabase servers located in <strong>Frankfurt, Germany</strong>. The legal basis for this processing is the performance of a contract (Art. 6 (1) lit. b GDPR).
                </p>
              </div>

              <div className="p-6 bg-[#f9f9f9] border-2 border-black brutal-shadow-sm">
                <h3 className="font-black uppercase text-sm mb-2">SendFox (Newsletter)</h3>
                <p className="text-base">
                  We use SendFox, a service provided by Sumo Group Inc., 1305 E 6th St #3, Austin, TX 78702, USA, to send our newsletters. Data is transferred to SendFox and stored on their servers in the USA based on your explicit consent (Art. 6 (1) lit. a GDPR). You can revoke this via the "unsubscribe" link at any time.
                </p>
              </div>

              <div className="p-6 bg-[#f9f9f9] border-2 border-black brutal-shadow-sm">
                <h3 className="font-black uppercase text-sm mb-2">Google LLC (Authentication)</h3>
                <p className="text-base">
                  Authentication via Google account is handled by Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA. Google shares your email and basic profile info with us. This data may be processed on servers in the United States. The legal basis is the performance of a contract (Art. 6 (1) lit. b GDPR).
                </p>
              </div>

              <div className="p-6 bg-[#f9f9f9] border-2 border-black brutal-shadow-sm">
                <h3 className="font-black uppercase text-sm mb-2">Stripe (Payment Processing)</h3>
                <p className="text-base">
                  We use Stripe for secure payments. Payment data (credit card details/billing address) is processed directly by Stripe, Inc. ETERA does not store full credit card numbers. The legal basis for this processing is the performance of a contract (Art. 6 (1) lit. b GDPR).
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-black uppercase mb-6 text-black tracking-tight">
              5. Your Rights and Choices
            </h2>
            <p className="mb-4">You have full control over your personal data. At any time, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-3 font-bold">
              <li>Access, correct, or delete your personal information.</li>
              <li>Opt-out of marketing emails by clicking the "unsubscribe" link at the bottom of any newsletter sent via SendFox.</li>
              <li>
                <strong>Completely erase your account:</strong> To delete your ETERA account and permanently wipe all associated QR codes and analytics, contact us at <span className="text-blue-600">support@etera.store</span>.
              </li>
            </ul>
          </section>

          <section className="pt-8 border-t-4 border-black">
            <h2 className="text-2xl font-black uppercase mb-4 text-black">6. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy, please reach out to us at:
              <br />
              <a href="mailto:support@etera.store" className="text-blue-600 font-black hover:underline decoration-2 underline-offset-4">
                support@etera.store
              </a>
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t-4 border-black text-center">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-gray-400">
            ETERA.STORE — BUILT TO LAST.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;